import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Col,
  Card,
  Spin,
  Alert,
  Tag,
  Typography,
  Input,
  Select,
  Empty,
  Button
} from 'antd';
import {
  ShopOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import partnerService from '../../services/partnerService';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import FoundingPartnerBadge from '../../components/badges/FoundingPartnerBadge';
import { getCityByLanguage, formatCityCountry } from '../../utils/locationFormat';
import './PartnersListPage.scss';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const PartnersListPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    setLoading(true);
    setError(null);

    const result = await partnerService.fetchPartners();

    if (result.success) {
      const partnersList = result.data.results || result.data || [];
      setPartners(partnersList);

      // Extract unique cities for filter (case-insensitive to avoid duplicates)
      const cityMap = new Map();
      partnersList.forEach(p => {
        if (p.city) {
          const translated = getCityByLanguage(p.city, i18n.language);
          const key = translated.toLowerCase();
          if (!cityMap.has(key)) {
            cityMap.set(key, { original: p.city, translated });
          }
        }
      });
      setCities([...cityMap.values()].sort((a, b) => a.translated.localeCompare(b.translated, i18n.language)));
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Filter partners based on search and city
  const filteredPartners = partners.filter(partner => {
    const matchesSearch = !searchQuery ||
      partner.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.city?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCity = !cityFilter || partner.city === cityFilter;

    return matchesSearch && matchesCity;
  });

  const handleViewPartner = (partnerId) => {
    navigate(`/partners/${partnerId}`);
  };

  if (loading) {
    return (
      <div className="partners-list-page">
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="partners-list-page">
      <div className="page-header">
        <div className="header-content">
          <Title level={2}>{t('partners.title')}</Title>
          <Paragraph className="page-description">
            {t('partners.description')}
          </Paragraph>
        </div>
        <Button
          type="primary"
          icon={<GlobalOutlined />}
          onClick={() => navigate('/partners/map')}
          size="large"
        >
          {t('partners.viewOnMap')}
        </Button>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Filters */}
      <Card className="filters-card">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Search
              placeholder={t('partners.searchPlaceholder')}
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
              size="large"
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder={t('partners.filterByCity')}
              allowClear
              value={cityFilter}
              onChange={setCityFilter}
              style={{ width: '100%' }}
              size="large"
            >
              {cities.map(city => (
                <Option key={city.original} value={city.original}>{city.translated}</Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Partners Grid */}
      {filteredPartners.length === 0 ? (
        <Empty
          description={t('partners.noPartners')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Row gutter={[24, 24]}>
          {filteredPartners.map(partner => (
            <Col xs={24} sm={12} lg={8} xl={6} key={partner.id}>
              <Card
                className="partner-card"
                hoverable
                onClick={() => handleViewPartner(partner.id)}
              >
                <div className="partner-avatar">
                  {partner.logo ? (
                    <img src={partner.logo} alt={partner.store_name} />
                  ) : (
                    <ShopOutlined />
                  )}
                </div>

                <div className="partner-info">
                  <div className="partner-name-row">
                    <Title level={4} className="partner-name">
                      {partner.store_name}
                      {partner.segment === 'FOUNDING' && (
                        <FoundingPartnerBadge size="medium" />
                      )}
                    </Title>
                    {partner.is_verified && (
                      <Tag
                        color="green"
                        icon={<SafetyCertificateOutlined />}
                        className="verified-tag"
                      >
                        {t('partners.verified')}
                      </Tag>
                    )}
                    {partner.is_impact_partner && (
                      <Tag
                        color="magenta"
                        className="impact-tag"
                      >
                        🤝 {t('partners.impact')}
                      </Tag>
                    )}
                  </div>

                  <div className="partner-location">
                    <EnvironmentOutlined />
                    <Text>{formatCityCountry(partner.city, partner.country, i18n.language)}</Text>
                  </div>

                  {partner.service_categories && partner.service_categories.length > 0 && (
                    <div className="partner-categories">
                      {partner.service_categories.slice(0, 2).map((cat, idx) => (
                        <Tag key={idx} color="blue">
                          {i18n.language === 'el' ? (cat.name_el || cat.name_en) : cat.name_en}
                        </Tag>
                      ))}
                      {partner.service_categories.length > 2 && (
                        <Tag>+{partner.service_categories.length - 2}</Tag>
                      )}
                    </div>
                  )}
                </div>

                <div className="partner-actions" onClick={(e) => e.stopPropagation()}>
                  <FavoriteButton type="partner" itemId={partner.id} />
                  <Button type="primary" onClick={() => handleViewPartner(partner.id)}>
                    {t('partners.viewProfile')}
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default PartnersListPage;
